import React, { useState } from 'react';
import { FiCreditCard, FiFilter, FiSearch, FiChevronDown, FiCheck, FiX, FiEye, FiDownload, FiClock } from 'react-icons/fi';
import AdminLayout from '../components/layout/AdminLayout';

const AdminTransactions: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortOption, setSortOption] = useState('newest');
  
  // Mock data
  const transactions = [
    { 
      id: 'TRX-78901',
      user: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      amount: 149.99,
      status: 'completed',
      method: 'Credit Card',
      date: '2023-03-15 14:30:22',
      service: 'Financial Advisory'
    },
    { 
      id: 'TRX-78902',
      user: 'Michael Chen',
      email: 'michael.c@example.com',
      amount: 89.99,
      status: 'processing',
      method: 'PayPal',
      date: '2023-03-14 09:45:36',
      service: 'Portfolio Review'
    },
    { 
      id: 'TRX-78903',
      user: 'Emily Rodriguez',
      email: 'emily.r@example.com',
      amount: 249.99,
      status: 'completed',
      method: 'Bank Transfer',
      date: '2023-03-13 16:22:48',
      service: 'Retirement Planning'
    },
    { 
      id: 'TRX-78904',
      user: 'David Kim',
      email: 'david.k@example.com',
      amount: 74.99,
      status: 'failed',
      method: 'Credit Card',
      date: '2023-03-12 11:05:31',
      service: 'Tax Consultation'
    },
    { 
      id: 'TRX-78905',
      user: 'Jessica Lee',
      email: 'jessica.l@example.com',
      amount: 199.99,
      status: 'completed',
      method: 'PayPal',
      date: '2023-03-11 08:17:55',
      service: 'Investment Strategy'
    },
    { 
      id: 'TRX-78906',
      user: 'Robert Wilson',
      email: 'robert.w@example.com',
      amount: 124.99,
      status: 'refunded',
      method: 'Credit Card',
      date: '2023-03-10 13:40:12',
      service: 'Financial Advisory'
    },
    { 
      id: 'TRX-78907',
      user: 'Amanda Garcia',
      email: 'amanda.g@example.com',
      amount: 299.99,
      status: 'completed',
      method: 'Bank Transfer',
      date: '2023-03-09 15:35:27',
      service: 'Estate Planning'
    }
  ];
  
  // Filter transactions based on search, status, date
  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      transaction.user.toLowerCase().includes(searchTerm.toLowerCase()) || 
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' || 
      transaction.status === filterStatus;
    
    // Simple date filter (in a real app, parse actual dates)
    let matchesDate = true;
    if (dateRange === 'today') {
      matchesDate = transaction.date.includes('2023-03-15');
    } else if (dateRange === 'week') {
      matchesDate = true; // Mock data all in the same week
    } else if (dateRange === 'month') {
      matchesDate = true; // Mock data all in the same month
    }
    
    return matchesSearch && matchesFilter && matchesDate;
  });
  
  // Sort transactions
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    if (sortOption === 'newest') {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortOption === 'oldest') {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    } else if (sortOption === 'highest') {
      return b.amount - a.amount;
    } else if (sortOption === 'lowest') {
      return a.amount - b.amount;
    }
    return 0;
  });
  
  const getStatusBadgeClass = (status: string) => {
    switch(status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed':
        return <FiCheck className="text-green-500" size={16} />;
      case 'processing':
        return <FiCreditCard className="text-blue-500" size={16} />;
      case 'failed':
        return <FiX className="text-red-500" size={16} />;
      case 'refunded':
        return <FiChevronDown className="text-yellow-500" size={16} />;
      default:
        return null;
    }
  };
  
  // Summary stats
  const transactionStats = {
    totalValue: transactions.reduce((sum, transaction) => sum + transaction.amount, 0),
    completed: transactions.filter(t => t.status === 'completed').length,
    processing: transactions.filter(t => t.status === 'processing').length,
    failed: transactions.filter(t => t.status === 'failed').length
  };
  
  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold">Transaction Management</h1>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
            <button className="bg-primary text-white rounded-lg py-2 px-4 hover:bg-primary/90 transition-colors flex items-center">
              <FiDownload className="mr-2" size={16} />
              Export Transactions
            </button>
          </div>
        </div>
        
        {/* Stats overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-foreground border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-primary/10 mr-4">
                <FiCreditCard size={20} />
              </div>
              <div>
                <p className="text-text/70 text-sm">Total Value</p>
                <h3 className="text-xl font-bold mt-1">${transactionStats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
              </div>
            </div>
          </div>
          
          <div className="bg-foreground border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-green-100 mr-4">
                <FiCheck size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-text/70 text-sm">Completed</p>
                <h3 className="text-xl font-bold mt-1">{transactionStats.completed}</h3>
              </div>
            </div>
          </div>
          
          <div className="bg-foreground border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-blue-100 mr-4">
                <FiCreditCard size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-text/70 text-sm">Processing</p>
                <h3 className="text-xl font-bold mt-1">{transactionStats.processing}</h3>
              </div>
            </div>
          </div>
          
          <div className="bg-foreground border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-red-100 mr-4">
                <FiX size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-text/70 text-sm">Failed</p>
                <h3 className="text-xl font-bold mt-1">{transactionStats.failed}</h3>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-foreground border border-border rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiSearch className="text-gray-400" size={16} />
                </div>
                <input
                  type="text"
                  className="bg-card/30 border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiFilter className="text-gray-400" size={16} />
                </div>
                <select
                  className="bg-card/30 border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiClock className="text-gray-400" size={16} />
                </div>
                <select
                  className="bg-card/30 border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiChevronDown className="text-gray-400" size={16} />
                </div>
                <select
                  className="bg-card/30 border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Amount</option>
                  <option value="lowest">Lowest Amount</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {/* Transactions Table */}
        <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-card/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-card/30">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {transaction.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-3">
                          {transaction.user.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium">{transaction.user}</div>
                          <div className="text-text/50 text-sm">{transaction.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      ${transaction.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(transaction.status)}
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {transaction.method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text/70">
                      {transaction.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button className="p-1 text-primary hover:text-primary/80 transition-colors" title="View Details">
                          <FiEye size={18} />
                        </button>
                        <button className="p-1 text-blue-500 hover:text-blue-700 transition-colors" title="Download Receipt">
                          <FiDownload size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredTransactions.length === 0 && (
            <div className="px-6 py-8 text-center">
              <p className="text-text/50">No transactions found matching your search criteria.</p>
            </div>
          )}
          
          <div className="px-6 py-3 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text/70">
              Showing {filteredTransactions.length} of {transactions.length} transactions
            </p>
            
            <div className="flex">
              <button className="px-3 py-1 rounded-md bg-card/50 text-sm mr-2 hover:bg-card-hover">
                Previous
              </button>
              <button className="px-3 py-1 rounded-md bg-primary text-white text-sm mr-2">
                1
              </button>
              <button className="px-3 py-1 rounded-md bg-card/50 text-sm">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTransactions; 